import { createContext, useContext, useMemo, type ReactNode } from 'react'

import {
  taxonomyFor,
  disciplineFromProperties,
  type DrawingDiscipline,
  type Taxonomy,
} from '@/config/classification'

/**
 * The active drawing taxonomy for everything rendered inside the editor.
 *
 * A context rather than a prop: the Category / Subcategory / Type dropdowns are
 * spread across ~8 panels at varying depths, and threading a `taxonomy` prop
 * through every one of them would touch far more code than it explains. Panels
 * call `useTaxonomy()`; only the editor root decides which one is active.
 *
 * Defaults to P&ID when no provider is mounted, so a panel rendered outside the
 * editor (a board preview, a test) behaves exactly as it did before SLD existed.
 */
const TaxonomyContext = createContext<Taxonomy>(taxonomyFor('pnid'))

export function useTaxonomy(): Taxonomy {
  return useContext(TaxonomyContext)
}

interface TaxonomyProviderProps {
  /** The graph document's `properties`; the discipline is read from it. */
  graphProperties?: Record<string, unknown> | null
  /** Explicit override, for tests and for callers that already know. */
  discipline?: DrawingDiscipline
  children: ReactNode
}

export function TaxonomyProvider({
  graphProperties,
  discipline,
  children,
}: TaxonomyProviderProps) {
  const value = useMemo(
    () => taxonomyFor(discipline ?? disciplineFromProperties(graphProperties)),
    [discipline, graphProperties],
  )
  return <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>
}

export { TaxonomyContext }
